import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitSubscribe } from '../../api/subscribeApi';
import { useRecaptcha } from '../shared/useRecaptcha';
import type { SubscribeCampaignConfig } from '../../config/types';
import type { SubscribeFormValues } from './SubscribeFormSchema';
import type { SubscribeSuccessState } from './types';

/**
 * Submission logic isolated from rendering: any layout — the 3 built-in templates, or a
 * brand-new one-off page — can call this same hook to get the exact same WLS/reCAPTCHA
 * wiring without re-implementing it.
 *
 * Success navigates to a dedicated /dziekujemy route (see router.tsx) — matches the base
 * Symfony app's behavior. Errors show a Modal instead of navigating to /blad: navigating away
 * unmounts the form and loses whatever the user had already typed, which matters more for a
 * transient failure the user may just want to retry. /blad still exists as a route for direct
 * links, but the submit flow itself no longer uses it.
 */
export function useSubscribeSubmit(config: SubscribeCampaignConfig) {
  const navigate = useNavigate();
  const recaptcha = useRecaptcha(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  const onSubmit = async (values: SubscribeFormValues) => {
    setIsSubmitting(true);
    try {
      const recaptchaToken = await recaptcha.execute();
      const result = await submitSubscribe({ slug: config.slug, ...values, recaptchaToken });

      if (result.status === 'ok' && result.orderId) {
        const state: SubscribeSuccessState = {
          formName: result.formName ?? config.formName ?? undefined,
          orderId: result.orderId,
          leadId: result.leadId,
          email: values.email,
          phone: values.phone,
          firstName: values.firstName,
          lastName: values.lastName,
          agreeNewsletter: values.agreeNewsletter,
          agreeTestDrive: values.agreeTestDrive,
          extraCheckbox1: values.extraCheckbox1,
          extraCheckbox2: values.extraCheckbox2,
          date: values.date,
          time: values.time,
        };
        navigate(`/${config.slug}/dziekujemy`, { state });
      } else {
        setErrorOpen(true);
      }
    } catch {
      setErrorOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { onSubmit, isSubmitting, recaptchaRef: recaptcha.containerRef, errorOpen, setErrorOpen };
}
